/**
 * IntakeService — contractor & partner intake form persistence
 *
 * All reads and writes go through this service. Components must never call
 * Supabase directly. Every write includes organization_id sourced from
 * getCurrentOrganizationId().
 *
 * Reference pattern: src/services/lead.service.ts
 */

import { supabase } from "@/lib/supabase";
import { getCurrentOrganizationId } from "@/helpers/auth.helpers";
import { SystemUpdateService } from '@/services/system-update.service';

// ── Types ──────────────────────────────────────────────────────────────────────

export type IntakePath = "grow" | "find_work" | "partner";

export type IntakeStatus =
  | "new"
  | "under_review"
  | "qualified"
  | "follow_up"
  | "onboarding"
  | "rejected"
  | "archived"
  | "closed";

export interface IntakeSubmission {
  id: string;
  organizationId: string;
  path: IntakePath;
  status: IntakeStatus;
  name: string;
  email: string;
  phone?: string;
  companyName?: string;
  message?: string;
  internalNotes?: string;
  reviewedBy?: string;
  reviewedAt?: string;
  /** Path-specific extra fields (lookingFor[], crewName, tradeType, etc.) */
  metadata: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export type IntakeSubmissionCreate = Omit<
  IntakeSubmission,
  "id" | "organizationId" | "createdAt" | "updatedAt"
>;

// ── DB row ↔ TS model mapping ──────────────────────────────────────────────────

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function fromRow(row: Record<string, any>): IntakeSubmission {
  return {
    id: row.id,
    organizationId: row.organization_id,
    path: row.path as IntakePath,
    status: row.status as IntakeStatus,
    name: row.name,
    email: row.email,
    phone: row.phone ?? undefined,
    companyName: row.company_name ?? undefined,
    message: row.message ?? undefined,
    internalNotes: row.internal_notes ?? undefined,
    reviewedBy: row.reviewed_by ?? undefined,
    reviewedAt: row.reviewed_at ?? undefined,
    metadata: (row.metadata as Record<string, unknown>) ?? {},
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

function toInsertRow(
  input: IntakeSubmissionCreate,
  organizationId: string,
): Record<string, unknown> {
  return {
    organization_id: organizationId,
    path: input.path,
    status: input.status ?? "new",
    name: input.name,
    email: input.email,
    phone: input.phone ?? null,
    company_name: input.companyName ?? null,
    message: input.message ?? null,
    internal_notes: input.internalNotes ?? null,
    reviewed_by: input.reviewedBy ?? null,
    reviewed_at: input.reviewedAt ?? null,
    metadata: input.metadata ?? {},
  };
}

function toPersistedStatus(status: IntakeStatus): IntakeStatus {
  // Keep UI-friendly archived status while preserving DB compatibility where
  // "closed" may be the canonical terminal value.
  if (status === "archived") return "closed";
  return status;
}

// ── Service ────────────────────────────────────────────────────────────────────

export const IntakeService = {
  /**
   * Load all intake submissions for the current org, newest first.
   */
  async getAll(): Promise<IntakeSubmission[]> {
    const organizationId = await getCurrentOrganizationId();

    const { data, error } = await supabase
      .from("contractor_intake_submissions")
      .select("*")
      .eq("organization_id", organizationId)
      .order("created_at", { ascending: false });

    if (error) {
      console.error("IntakeService.getAll:", error);
      return [];
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (data ?? []).map((row: any) => fromRow(row));
  },

  /**
   * Create a new intake submission.
   * Returns the created record or null on error.
   */
  async create(
    input: IntakeSubmissionCreate,
  ): Promise<IntakeSubmission | null> {
    const organizationId = await getCurrentOrganizationId();
    const row = toInsertRow(input, organizationId);

    const { data, error } = await supabase
      .from("contractor_intake_submissions")
      .insert([row])
      .select()
      .single();

    if (error) {
      console.error("IntakeService.create:", error);
      return null;
    }

    const created = fromRow(data as any); // eslint-disable-line @typescript-eslint/no-explicit-any
    const applicantName = created.companyName ?? created.name;
    void SystemUpdateService.post({
      domain:   'onboarding',
      event:    'application_submitted',
      body:     `System: Onboarding Application Submitted — ${applicantName}`,
      metadata: {
        title:  'Onboarding Application Submitted',
        detail: applicantName,
        action: { label: 'View Onboarding', path: '/contractor-onboarding' },
      },
    });
    return created;
  },

  /**
   * Update the status of a submission. Optionally attach an internal note.
   * Also stamps reviewed_by if notes are provided.
   */
  async updateStatus(
    id: string,
    status: IntakeStatus,
    notes?: string,
    reviewedBy?: string,
  ): Promise<void> {
    const updates: Record<string, unknown> = {
      status: toPersistedStatus(status),
      updated_at: new Date().toISOString(),
    };

    if (notes !== undefined) {
      updates.internal_notes = notes;
    }

    if (reviewedBy !== undefined) {
      updates.reviewed_by = reviewedBy;
      updates.reviewed_at = new Date().toISOString();
    }

    const { error } = await supabase
      .from("contractor_intake_submissions")
      .update(updates)
      .eq("id", id);

    if (error) {
      console.error("IntakeService.updateStatus:", error);
      throw error;
    }

    if (status === 'onboarding') {
      void SystemUpdateService.post({
        domain:   'onboarding',
        event:    'onboarding_approved',
        body:     `System: Onboarding Approved`,
        metadata: {
          title:  'Onboarding Approved',
          detail: `Intake ${id}`,
          action: { label: 'View Onboarding', path: '/contractor-onboarding' },
        },
      });
    } else if (status === 'rejected') {
      void SystemUpdateService.post({
        domain: 'onboarding',
        event:  'onboarding_rejected',
        body:   `System: Onboarding Rejected — Intake ${id}`,
      });
    }
  },

  /**
   * Save internal review notes for a submission.
   */
  async addNote(
    id: string,
    note: string,
    reviewedBy: string,
  ): Promise<void> {
    const { error } = await supabase
      .from("contractor_intake_submissions")
      .update({
        internal_notes: note,
        reviewed_by: reviewedBy,
        reviewed_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      })
      .eq("id", id);

    if (error) {
      console.error("IntakeService.addNote:", error);
      throw error;
    }
  },

  /**
   * Delete a submission (admin use only).
   */
  async delete(id: string): Promise<void> {
    const { error } = await supabase
      .from("contractor_intake_submissions")
      .delete()
      .eq("id", id);

    if (error) {
      console.error("IntakeService.delete:", error);
      throw error;
    }
  },
};
